import { select, event, timeFormat } from "d3";
import {
  bandsCreate,
  hBandsCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  portfolioTooltip,
  detailPortfolioTooltip,
  planRateM2MTooltip,
  populateTable,
  historicalTooltip,
  accumHistoricalTooltip,
  accumHistoricalTooltipSm,
} from "@/_core/charts/D3ChartAnalyticsChartHelpers";
import {
  colorDarkExpo,
  darkBackground4,
  grayLightBackground,
  textactive2,
  textactive,
} from "@/_core/_helpers/_parametric_tools";
import { ChartZoomY } from "../refactor/ChartZoomY";

// ==== Custom functions for Portfolio =======================================================
export const D3HoverPortfolio = function() {};

D3HoverPortfolio.prototype.hoverFct = function(
  svg,
  data,
  customName,
  formatNumber,
  currency,
  showTooltip,
  axisFormatNumber = null,
  colorTheme,
) {
  if (axisFormatNumber === null) {
    axisFormatNumber = formatNumber;
  }
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let tooltipWidth2 = 120;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("background", darkBackground4(colorTheme))
    .style("width", 400 + "px")
    .html("");

  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", "auto")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    select(".hHoverLinePORT")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    select(".bandsPORT" + i).classed("hoved", true);
    select(".barPORT" + i).classed("hoved", true);
    select(".barPORTE" + i).classed("hoved", true);
    select(".barPORTH" + i).classed("hoved", true);
    select(".barPORTP" + i).classed("hoved", true);

    if (showTooltip) {
      let position = d.expo === 0 ? "" : d.expo < 0 ? "BUY " : "SELL ";
      let sgn = Math.sign(d.expo);
      sgn = sgn === 0 ? 1 : sgn;
      let highlight = d.date === data[0].date ? 1 : 2;
      let txt = portfolioTooltip(
        data,
        currency,
        position,
        formatNumber,
        sgn,
        highlight,
      );

      Tooltip.style("opacity", 1)
        .style(
          "left",
          svg.margin.left + svg.x(d.date) + svg.x.bandwidth() / 2 + 5 + "px",
        )
        .style("top", yPixels + 20 + "px")
        .html(txt);
    }

    Tooltip2.style("opacity", 1)
      .style("right", svg.width + svg.margin.right + 1 + "px")
      .style("top", yPixels + svg.margin.top - 11 + "px")
      .html(axisFormatNumber(svg.y.invert(yPixels)));
    Tooltip3.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(d.date);
  };

  var mouseLeave = function(d, i) {
    select(".hHoverLinePORT")
      .style("opacity", 0)
      .attr("display", null);

    if (showTooltip) {
      Tooltip.style("left", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
    }

    Tooltip2.style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".bandsPORT" + i).classed("hoved", false);
    select(".barPORT" + i).classed("hoved", false);
    select(".barPORTE" + i).classed("hoved", false);
    select(".barPORTH" + i).classed("hoved", false);
    select(".barPORTP" + i).classed("hoved", false);
  };

  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);
};

// ==== Custom functions for Portfolio =======================================================
export const D3HoverDetailPortfolio = function() {};

D3HoverDetailPortfolio.prototype.hoverFct = function(
  svg,
  data,
  customName,
  formatNumber,
  formatPercent,
  currency,
  hedge,
  program,
  showTooltip,
  axisFormatNumber = null,
  hedgeColor,
  expoColor = null,
  asset = "currencies",
  colorTheme,
  fixing_type,
) {
  if (expoColor === null) {
    expoColor = hedgeColor;
  }
  if (axisFormatNumber === null) {
    axisFormatNumber = formatNumber;
  }
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let tooltipWidth3 = 145;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("text-align", "right")
    .style("padding", "2px 8px 2px 2px")
    .attr("class", "tooltip")
    .style("font-size", "0.7rem")
    .style("width", tooltipWidth3 + "px")
    .html("");
  let tooltipWidth2 = 70;
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("background", darkBackground4(colorTheme))
    .style("width", 400 + "px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(event.layerX - svg.margin.left, svg.width),
      0,
    );
    const yPixels = event.layerY;
    select(".vHoverLineDETAIL")
      .style("opacity", 1)
      .attr("x1", xPixels)
      .attr("x2", xPixels);

    select(".bandsDETAIL" + i).classed("hoved", true);
    select(".hbarEXPO" + d.id).classed("hoved", true);
    select(".hbarHEDGE" + d.id).classed("hoved", true);
    select(".hbarPART" + d.id).classed("hoved", true);
    if (d.id < 7) {
      select(".hedgeLine").style("stroke", textactive(colorTheme));
    } else {
      select(".expoLine").style("stroke", textactive(colorTheme));
    }

    if (showTooltip) {
      let position = data[0].sign < 0 ? "Buy " : "Sell ";
      let sgn = 1; //data[0].sign;
      let txt = detailPortfolioTooltip(
        data,
        hedge,
        program,
        currency,
        sgn,
        position,
        formatNumber,
        formatPercent,
        asset,
        d,
        fixing_type,
      );

      Tooltip.style("opacity", 1)
        .style("left", xPixels + svg.margin.left + 5 + "px")
        .style(
          "bottom",
          svg.height +
            svg.margin.top +
            svg.margin.bottom -
            yPixels -
            150 +
            "px",
        )
        .html(txt);
    }

    Tooltip2.style("opacity", 1)
      .style("left", xPixels + svg.margin.left - tooltipWidth2 + "px")
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(axisFormatNumber(svg.x.invert(xPixels)));

    Tooltip3.style("opacity", 1)
      .style("right", svg.width + svg.margin.right + 1 + "px")
      .style(
        "top",
        svg.y(d.y) + svg.y.bandwidth() / 2 + svg.margin.top - 11 + "px",
      )
      .html(d.y);
  };

  var mouseLeave = function(d, i) {
    select(".vHoverLineDETAIL")
      .style("opacity", 0)
      .attr("display", null);
    if (showTooltip) {
      Tooltip.style("left", "-1000000px")
        .style("bottom", "0px")
        .style("opacity", 0)
        .html("");
    }
    Tooltip2.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("right", "0px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".bandsDETAIL" + i).classed("hoved", false);
    select(".hbarEXPO" + d.id).classed("hoved", false);
    select(".hbarHEDGE" + d.id).classed("hoved", false);
    select(".hbarPART" + d.id).classed("hoved", false);
    select(".hedgeLine").style("stroke", hedgeColor);
    select(".expoLine").style("stroke", expoColor);
  };

  vLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  hBandsCreate(data, svg, customName, "y", mouseOver, mouseLeave);
};

// ==== Custom functions for Historical =======================================================
export const D3HoverHistorical = function() {};

D3HoverHistorical.prototype.hoverFct = function(
  svg,
  data,
  customName,
  formatNumber,
  dateFormat,
  title,
  axisFormatNumber = null,
  showOHL = true,
  colorTheme,
  isZoomY = false,
  figuresElements = [{}],
  renderChart = "",
  dataRender = [],
  toZero = false,
  _this = {},
  isZoomSync = false,
) {
  let txtTitle =
    title === "DAILY" ? "Day" : title === "WEEKLY" ? "Week" : "Month";
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip tooltip-transparent")
    .style("background", darkBackground4(colorTheme))
    .style("font-size", "0.5rem")
    .style("width", svg.width + "px")
    .style("line-height", "1")
    .html("");
  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("font-size", "0.8rem")
    .style("width", 55 + "px")
    .html("");

  let tooltipWidth2 = 80;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("text-align", "center")
    .style("padding", "2px")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );

    select(".hHoverLineTA")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);
    // === rates chart
    select(".hHoverLineRATES")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    select(".bandsTA" + i).classed("hoved", true);
    select(".stickTA" + i).classed("hoved", true);
    select(".stick2TA" + i).classed("hoved", true);
    select(".candleTA" + i).classed("hoved", true);

    // === for accumHistChart
    select(".bandsACC" + i).classed("hoved", true);
    select(".stackedBar1ACCH" + i).classed("hoved", true);
    select(".stackedBar2ACC" + i).classed("hoved", true);
    select(".barACCH" + i).classed("hoved", true);
    select(".barACC" + i).classed("hoved", true);

    let txt = historicalTooltip(d, txtTitle, dateFormat, formatNumber, showOHL);

    Tooltip.style("opacity", 1)
      .style("left", svg.margin.left + 1 + "px")
      .style("top", svg.margin.top + 1 + "px")
      .html(txt);

    Tooltip2.style("opacity", 1)
      .style("right", svg.width + svg.margin.right + 1 + "px")
      .style("top", yPixels + svg.margin.top - 11 + "px")
      .html(axisFormatNumber(svg.y.invert(yPixels)));

    Tooltip3.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(timeFormat(dateFormat)(d.date));
  };

  var mouseLeave = function(d, i) {
    select(".hHoverLineTA")
      .style("opacity", 0)
      .attr("display", null);
    // === rates chart
    select(".hHoverLineRATES")
      .style("opacity", 0)
      .attr("display", null);

    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip2.style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".bandsTA" + i).classed("hoved", false);
    select(".stickTA" + i).classed("hoved", false);
    select(".stick2TA" + i).classed("hoved", false);
    select(".candleTA" + i).classed("hoved", false);

    // === for accumHistChart
    select(".bandsACC" + i).classed("hoved", false);
    select(".stackedBar1ACCH" + i).classed("hoved", false);
    select(".stackedBar2ACC" + i).classed("hoved", false);
    select(".barACCH" + i).classed("hoved", false);
    select(".barACC" + i).classed("hoved", false);
  };

  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  if (isZoomY) {
    const zoomY = new ChartZoomY({
      svg,
      _this,
      figuresElements,
      data: dataRender,
      render: renderChart,
      heightResizeZoom: toZero,
      isZoomSync,
    });
    zoomY.getTypeEventZoomY();
  }
};

// ==== Custom functions for AccumHistoryChart =======================================================
export const D3HoverAccumHist = function() {};

D3HoverAccumHist.prototype.hoverFct = function(
  svg,
  data,
  customName,
  formatNumber,
  currency,
  dateFormat,
  currencyFormat,
  showAcc,
  showHedge,
  showTooltip,
  title,
  axisFormatNumber = null,
  colorTheme,
) {
  if (axisFormatNumber === null) {
    axisFormatNumber = formatNumber;
  }
  let txtTitle =
    title === "DAILY" ? "DAY" : title === "WEEKLY" ? "WEEK" : "MONTH";
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let Tooltip;
  if (showTooltip) {
    Tooltip = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", "tooltip")
      .style("background", darkBackground4(colorTheme))
      .style("font-size", "0.8rem")
      .style("width", 333 + "px")
      .html("");
  } else {
    Tooltip = select(`${sharp}${svg.parentElement}`)
      .append("div")
      .style("opacity", 0)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("padding", "2px")
      .attr("class", "tooltip tooltip-transparent")
      .style("background", darkBackground4(colorTheme))
      .style("font-size", "0.5rem")
      .style("width", svg.width + "px")
      .style("line-height", "1")
      .html("");
  }

  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", 55 + "px")
    .html("");

  let tooltipWidth2 = 80;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", tooltipWidth2 + "px")
    .html("");

  var mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );

    select(".hHoverLineACC")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    select(".bandsACC" + i).classed("hoved", true);
    if (showAcc && showHedge) {
      select(".stackedBar1ACCH" + i).classed("hoved", true);
      select(".stackedBar2ACC" + i).classed("hoved", true);
    } else if (showHedge) {
      select(".barACCH" + i).classed("hoved", true);
    } else if (showAcc) {
      select(".barACC" + i).classed("hoved", true);
    }

    // === for historical rates
    select(".bandsTA" + i).classed("hoved", true);
    select(".stickTA" + i).classed("hoved", true);
    select(".stick2TA" + i).classed("hoved", true);
    select(".candleTA" + i).classed("hoved", true);

    let rateStr = Number.isFinite(d.m2m_rate)
      ? currencyFormat(d.m2m_rate)
      : "-";
    let total = showHedge ? d.tot + d.hedge_m2m : d.tot;
    let txt;
    if (showTooltip) {
      txt = accumHistoricalTooltip(
        d,
        currency,
        rateStr,
        dateFormat,
        formatNumber,
        total,
        txtTitle,
      );

      Tooltip.style("opacity", 1)
        .style(
          "left",
          svg.margin.left + svg.x(d.date) + svg.x.bandwidth() / 2 + 5 + "px",
        )
        .style("top", null)
        .style(
          "bottom",
          svg.height - yPixels - svg.margin.top + svg.margin.bottom + 12 + "px",
        )
        .html(txt);
    } else {
      txt = accumHistoricalTooltipSm(
        d,
        txtTitle,
        rateStr,
        dateFormat,
        axisFormatNumber,
        total,
      );
      Tooltip.style("opacity", 1)
        .style("left", svg.margin.left + 1 + "px")
        .style("top", svg.margin.top + 1 + "px")
        .style("bottom", null)
        .html(txt);
    }

    Tooltip2.style("opacity", 1)
      .style("right", svg.width + svg.margin.right + 1 + "px")
      .style("top", yPixels + svg.margin.top - 11 + "px")
      .html(axisFormatNumber(svg.y.invert(yPixels)));

    Tooltip3.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) +
          svg.x.bandwidth() / 2 +
          svg.margin.left -
          tooltipWidth2 / 2 +
          "px",
      )
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(timeFormat(dateFormat)(d.date));
  };

  var mouseLeave = function(d, i) {
    select(".hHoverLineACC")
      .style("opacity", 0)
      .attr("display", null);

    Tooltip.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip2.style("right", 0)
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".bandsACC" + i).classed("hoved", false);
    if (showAcc && showHedge) {
      select(".stackedBar1ACCH" + i).classed("hoved", false);
      select(".stackedBar2ACC" + i).classed("hoved", false);
    } else if (showHedge) {
      select(".barACCH" + i).classed("hoved", false);
    } else if (showAcc) {
      select(".barACC" + i).classed("hoved", false);
    }

    // === for historical rates
    select(".bandsTA" + i).classed("hoved", false);
    select(".stickTA" + i).classed("hoved", false);
    select(".stick2TA" + i).classed("hoved", false);
    select(".candleTA" + i).classed("hoved", false);
  };

  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);
};

// ==== Custom functions for Planning =======================================================
export const D3HoverPlanning = function() {};

D3HoverPlanning.prototype.hoverFct = function(
  svg,
  data,
  customName,
  formatNumber,
  currency,
  axisFormatNumber,
  dateFormat,
  currencyFormat,
  budgetRate,
  formatPercent,
  showTooltip,
  formatM,
  averages,
  showAve,
  asset = "currencies",
  isFuture = true,
  colorTheme,
  isBeta = false,
  _this = [],
  idField = "id",
  isZoomY = false,
  figuresElements = [{}],
  renderChart = "",
  dataRender = [],
  toZero = false,
  barsName = [],
  isZoomSync = false,
  isAdvancedView = false,
) {
  let sharp = "#";
  select(`${sharp}${svg.parentElement}`)
    .selectAll(".tooltip")
    .remove();
  let tooltipWidth = 550;
  let Tooltip = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("background", darkBackground4(colorTheme))
    .style("width", tooltipWidth + "px")
    .html("");

  let Tooltip2 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", "0 auto")
    .style("text-align", "center")
    .style("padding", "2px")
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .attr("class", "tooltip")
    .style("font-size", "0.8rem")
    .style("width", 55 + "px")
    .html("");

  let tooltipWidth2 = 60;
  let Tooltip3 = select(`${sharp}${svg.parentElement}`)
    .append("div")
    .style("opacity", 0)
    .style("margin", 0)
    .style("background", colorDarkExpo(colorTheme))
    .style("border", colorDarkExpo(colorTheme))
    .style("color", textactive2(colorTheme))
    .style("text-align", "center")
    .style("padding", "2px")
    .attr("class", "tooltip")
    .style("width", tooltipWidth2 + "px")
    .html("");

  const mouseOver = function(d, i) {
    event.preventDefault();
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0,
    );
    select(".bandsRATES" + i).classed("hoved", true);
    select(".bandsPLAN" + i).classed("hoved", true);
    select(`.bands${customName}` + i).classed("hoved", true);
    select(`.bandspDerivatives` + i).classed("hoved", true);
    select(`.bandsqDerivatives` + i).classed("hoved", true);
    select(`.bandsrates` + i).classed("hoved", true);
    select(`.bandsplanning` + i).classed("hoved", true);

    if (customName === "PLAN") {
      select(".hHoverLinePLAN")
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);

      select(".barPLANB2" + +d[idField]).classed("hoved", true);
      select(".barPLANB" + +d[idField]).classed("hoved", true);
      select(".stackedBar1PLANB" + i).classed("hoved", true);
      select(".stackedBar2PLANCF" + i).classed("hoved", true);
      select(".barPLANH" + +d["date"]).classed("hoved", true);
      select(".barPLANP" + +d["date"]).classed("hoved", true);
      select(".stackedBar1PLANH" + +d[idField]).classed("hoved", true);
      select(".stackedBar2PLANOP" + +d[idField]).classed("hoved", true);
      select(".barPLANOP" + +d[idField]).classed("hoved", true);
      select(".stackedBar1PLANOP" + i).classed("hoved", true);
      select(".barPLANBudg" + +d[idField]).classed("hoved", true);
      select(".barPLANProg" + +d[idField]).classed("hoved", true);
      select(".barPLANCF" + +d[idField]).classed("hoved", true);
      select(".stackedBar2PLANH2" + +d[idField]).classed("hoved", true);
      select(".stackedBar2PLANHH" + +d[idField]).classed("hoved", true);
      select(".stackedBar2REQH2" + i).classed("hoved", true);
      select(".barPLANP" + +d[idField]).classed("hoved", true);
    } else if (customName === "RATES") {
      select(".hHoverLineRATES")
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);
      select(".hHoverLineTA")
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);

      // === for rates
      select(".barRATEB" + +d[idField]).classed("hoved", true);
      select(".barRATEE" + +d[idField]).classed("hoved", true);
      select(".barRATEC" + +d[idField]).classed("hoved", true);
      select(".barRATEH" + +d[idField]).classed("hoved", true);
      select(".barRATEP" + +d[idField]).classed("hoved", true);
      select(".barRATEN" + +d[idField]).classed("hoved", true);
      select(".barRATEE45" + +d["date"]).classed("hoved", true);
      select(".barRATEH2" + +d["date"]).classed("hoved", true);

      select(".barDLF" + +d[idField]).classed("hoved", true);
      select(".barDSF" + +d[idField]).classed("hoved", true);
      select(".barDLC" + +d[idField]).classed("hoved", true);
      select(".barDSC" + +d[idField]).classed("hoved", true);
      select(".barDLP" + +d[idField]).classed("hoved", true);
      select(".barDSP" + +d[idField]).classed("hoved", true);
      select(".barDLCKO" + +d[idField]).classed("hoved", true);
      select(".barDSCKO" + +d[idField]).classed("hoved", true);
      select(".barDLPKO" + +d[idField]).classed("hoved", true);
      select(".barDSPKO" + +d[idField]).classed("hoved", true);
    } else if (customName === "M2M") {
      select(".hHoverLineM2M")
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);
    }
    if (barsName) {
      select(`.hHoverLine${customName}`)
        .style("opacity", 1)
        .attr("y1", yPixels)
        .attr("y2", yPixels);

      barsName.forEach((bar) => {
        select(`.${bar}${+d[idField]}`).classed("hoved", true);
      });
    }

    // === for m2m
    select(".bandsM2M" + i).classed("hoved", true);
    select(".barM2ME" + i).classed("hoved", true);
    select(".barM2MH" + i).classed("hoved", true);
    select(".barM2MN" + i).classed("hoved", true);

    if (isBeta) {
      const variableAdvanced = _this.isPrices ? "RATE" : "PLAN";
      const variable = _this.isPrices ? "p_" : "q_";
      Object.assign(_this.formatVars, {
        userHovered: {
          date: +d[idField],
          variable: isAdvancedView ? variableAdvanced : variable,
          hovered: false,
          fromChart: true,
        },
      });
    }

    // === tooltip and table
    let posAu;

    if (!isBeta) {
      let position = d.balance < 0 ? "BUY " : d.balance === 0 ? "" : "SELL ";
      let sgn = Math.sign(d.balance);
      let positionCF =
        d.cash_flow < 0 ? "BUY " : d.cash_flow === 0 ? "" : "SELL ";
      let sgnCF = Math.sign(d.cash_flow);
      let total = d.balance + d.cash_flow;
      let positionTOT = total < 0 ? "BUY " : total === 0 ? "" : "SELL ";
      let sgnTOT = Math.sign(total);
      sgnTOT = sgnTOT === 0 ? Math.sign(d.hedge) : sgnTOT;
      let required = d.hedge >= 0 ? d.hedge - d.program : d.program - d.hedge;
      let requiredTxt = required > 0 ? "EXCESS HEDGE" : "REQUIRED HEDGE";

      // === table
      populateTable(
        d,
        positionTOT,
        sgnTOT,
        total,
        formatM,
        formatPercent,
        requiredTxt,
        required,
        currencyFormat,
        budgetRate,
        dateFormat,
      );
      if (showTooltip) {
        let txt = planRateM2MTooltip(
          d,
          formatNumber,
          currency,
          currencyFormat,
          budgetRate,
          formatPercent,
          position,
          sgn,
          positionCF,
          sgnCF,
          total,
          positionTOT,
          sgnTOT,
          required,
          requiredTxt,
          showAve,
          dateFormat,
          asset,
          isFuture,
        );

        posAu = svg.x(d.date) > 0.45 * svg.width ? tooltipWidth + 5 : -5;
        let scrnHght = document.getElementById("whole-page-div").clientHeight;
        let posTop, posBottom;
        let condition =
          svg.height > 0.5 * scrnHght
            ? yPixels < 0.45 * svg.height
            : customName === "PLAN"
            ? true
            : customName === "RATES"
            ? yPixels < 0.45 * svg.height
            : false;
        if (condition) {
          posTop = yPixels + svg.margin.top + 10 + "px";
          posBottom = null;
        } else {
          posTop = null;
          posBottom = svg.height + svg.margin.bottom - yPixels + 10 + "px";
        }

        Tooltip.style("opacity", 1)
          .style(
            "left",
            svg.margin.left +
              svg.x(d.date) +
              svg.x.bandwidth() / 2 -
              posAu +
              "px",
          )
          .style("top", posTop)
          .style("bottom", posBottom)
          .html(txt);
      }
    }

    Tooltip2.style("opacity", 1)
      .style("left", svg.width + svg.margin.left + 1 + "px")
      .style("top", yPixels + svg.margin.top - 11 + "px")
      .html(axisFormatNumber(svg.y.invert(yPixels)));

    posAu =
      showTooltip === false
        ? 0.5 * tooltipWidth2
        : svg.x(d.date) < 0.45 * svg.width
        ? tooltipWidth2
        : 0;
    Tooltip3.style("opacity", 1)
      .style(
        "left",
        svg.x(d.date) + svg.x.bandwidth() / 2 + svg.margin.left - posAu + "px",
      )
      .style("top", svg.height + svg.margin.top + 1 + "px")
      .html(timeFormat(dateFormat)(d.date));
  };

  const mouseLeave = function(d, i) {
    select(".bandsRATES" + i).classed("hoved", false);
    select(".bandsPLAN" + i).classed("hoved", false);
    select(`.bands${customName}` + i).classed("hoved", false);
    select(`.bandspDerivatives` + i).classed("hoved", false);
    select(`.bandsqDerivatives` + i).classed("hoved", false);
    select(`.bandsrates` + i).classed("hoved", false);
    select(`.bandsplanning` + i).classed("hoved", false);

    if (customName === "PLAN") {
      select(".hHoverLinePLAN")
        .style("opacity", 0)
        .attr("display", null);

      select(".barPLANB2" + +d[idField]).classed("hoved", false);
      select(".barPLANB" + +d[idField]).classed("hoved", false);
      select(".stackedBar1PLANB" + i).classed("hoved", false);
      select(".stackedBar2PLANCF" + i).classed("hoved", false);
      select(".barPLANH" + +d["date"]).classed("hoved", false);
      select(".barPLANP" + +d["date"]).classed("hoved", false);
      select(".stackedBar1PLANH" + +d[idField]).classed("hoved", false);
      select(".stackedBar2PLANOP" + +d[idField]).classed("hoved", false);
      select(".barPLANOP" + +d[idField]).classed("hoved", false);
      select(".stackedBar1PLANOP" + i).classed("hoved", false);
      select(".barPLANBudg" + +d[idField]).classed("hoved", false);
      select(".barPLANProg" + +d[idField]).classed("hoved", false);
      select(".barPLANCF" + +d[idField]).classed("hoved", false);
      select(".stackedBar2PLANHH" + +d[idField]).classed("hoved", false);
      select(".stackedBar2PLANH2" + i).classed("hoved", false);
      select(".stackedBar2REQH2" + i).classed("hoved", false);
      select(".barPLANP" + +d[idField]).classed("hoved", false);
    } else if (customName === "RATES") {
      select(".hHoverLineRATES")
        .style("opacity", 0)
        .attr("display", null);
      select(".hHoverLineTA")
        .style("opacity", 0)
        .attr("display", null);

      // === for rates
      select(".barRATEB" + +d[idField]).classed("hoved", false);
      select(".barRATEE" + +d[idField]).classed("hoved", false);
      select(".barRATEC" + +d[idField]).classed("hoved", false);
      select(".barRATEP" + +d[idField]).classed("hoved", false);
      select(".barRATEH" + +d[idField]).classed("hoved", false);
      select(".barRATEN" + +d[idField]).classed("hoved", false);
      select(".barRATEE45" + +d[idField]).classed("hoved", false);
      select(".barRATEH2" + +d[idField]).classed("hoved", false);
    } else if (customName === "M2M") {
      select(".hHoverLineM2M")
        .style("opacity", 0)
        .attr("display", null);
    }
    if (barsName) {
      select(`.hHoverLine${customName}`)
        .style("opacity", 0)
        .attr("display", null);

      barsName.forEach((bar) => {
        select(`.${bar}${+d[idField]}`).classed("hoved", false);
      });
    }
    if (showTooltip) {
      Tooltip.style("left", "-1000000px")
        .style("top", "0px")
        .style("opacity", 0)
        .html("");
    }
    Tooltip2.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    Tooltip3.style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".barDLF" + +d[idField]).classed("hoved", false);
    select(".barDSF" + +d[idField]).classed("hoved", false);
    select(".barDLC" + +d[idField]).classed("hoved", false);
    select(".barDSC" + +d[idField]).classed("hoved", false);
    select(".barDLP" + +d[idField]).classed("hoved", false);
    select(".barDSP" + +d[idField]).classed("hoved", false);
    select(".barDLCKO" + +d[idField]).classed("hoved", false);
    select(".barDSCKO" + +d[idField]).classed("hoved", false);
    select(".barDLPKO" + +d[idField]).classed("hoved", false);
    select(".barDSPKO" + +d[idField]).classed("hoved", false);
    // === for m2m
    select(".bandsM2M" + i).classed("hoved", false);
    select(".barM2ME" + i).classed("hoved", false);
    select(".barM2MH" + i).classed("hoved", false);
    select(".barM2MN" + i).classed("hoved", false);
    if (isBeta) {
      Object.assign(_this.formatVars, {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      });
    }
    // === tooltip and table
    if (!isBeta) {
      let total = averages.balance + averages.cash_flow;
      let positionTOT = total < 0 ? "BUY " : total === 0 ? "" : "SELL ";
      let sgnTOT = Math.sign(total);
      sgnTOT = sgnTOT === 0 ? Math.sign(averages.hedge) : sgnTOT;
      let required =
        averages.hedge >= 0
          ? averages.hedge - averages.program
          : averages.program - averages.hedge;
      let requiredTxt = required > 0 ? "EXCESS HEDGE" : "REQUIRED HEDGE";
      // === table
      populateTable(
        averages,
        positionTOT,
        sgnTOT,
        total,
        formatM,
        formatPercent,
        requiredTxt,
        required,
        currencyFormat,
        budgetRate,
        dateFormat,
      );
    }
  };

  // === tooltip and table
  if (!isBeta) {
    let total = averages.balance + averages.cash_flow;
    let positionTOT = total < 0 ? "BUY " : total === 0 ? "" : "SELL ";
    let sgnTOT = Math.sign(total);
    sgnTOT = sgnTOT === 0 ? Math.sign(averages.hedge) : sgnTOT;
    let required =
      averages.hedge >= 0
        ? averages.hedge - averages.program
        : averages.program - averages.hedge;
    let requiredTxt = required > 0 ? "EXCESS HEDGE" : "REQUIRED HEDGE";
    // === table
    populateTable(
      averages,
      positionTOT,
      sgnTOT,
      total,
      formatM,
      formatPercent,
      requiredTxt,
      required,
      currencyFormat,
      budgetRate,
      dateFormat,
    );
  }

  hLineCreate(svg, customName, grayLightBackground(colorTheme), 1);
  bandsCreate(data, svg, customName, mouseOver, mouseLeave);

  if (isZoomY) {
    const zoomY = new ChartZoomY({
      svg,
      _this,
      figuresElements,
      data: dataRender,
      render: renderChart,
      heightResizeZoom: toZero,
      isZoomSync,
    });
    zoomY.getTypeEventZoomY();
  }
};
